import { normalizeBarTimeSec } from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  type ChartSeriesSource,
  SERIES_MIN_TIMEFRAME_SECONDS,
  type SeriesInputValues,
  type SeriesPoint,
} from '@/lib/tradingview/datafeeds/series-sources'
import { fetchOrderbookMetricsSeries } from '@/service/orderbook-metrics.api'
import {
  buildOrderbookMetricsRequest,
  DEFAULT_DEPTH_TIER_BPS,
  isSupportedDepthTier,
  type OrderbookMetricsPoint,
  orderbookMetricsSeriesToPoints,
} from '@/service/orderbook-metrics.types'

/** Depth tier from the study's select input; validated so the request never
 *  round-trips a guaranteed BadRequest (buildOrderbookMetricsRequest throws). */
export function resolveDepthTierBps(inputs: SeriesInputValues | undefined): number {
  const tier = Number(inputs?.depthTierBps)
  return isSupportedDepthTier(tier) ? tier : DEFAULT_DEPTH_TIER_BPS
}

export function resolveOrderbookLiquidityMeasure(
  inputs: SeriesInputValues | undefined
): 'base' | 'quote' {
  return inputs?.measure === 'base' ? 'base' : 'quote'
}

/** tickMs is in MILLISECONDS (unlike CandleList.ticks) — normalize to seconds
 *  with the same rounding the candle cache uses. Depth imbalance is derived at
 *  the selected tier: (bidVal − askVal) / (bidVal + askVal). */
export function orderbookMetricsPointsToSeriesPoints(
  points: OrderbookMetricsPoint[],
  tierBps: number,
  measure: 'base' | 'quote' = 'quote'
): SeriesPoint[] {
  const result: SeriesPoint[] = []
  for (const point of points) {
    const timeSec = normalizeBarTimeSec(point.tickMs)
    if (!Number.isFinite(timeSec)) continue
    const values: SeriesPoint['values'] = {}
    const set = (metric: keyof SeriesPoint['values'], value: number) => {
      if (Number.isFinite(value)) values[metric] = value
    }
    set('obSpreadBps', point.spreadBps)
    set('obPressureRatio', point.pressureRatio)
    const liquidityDivisor = measure === 'base' ? point.midPrice : 1
    if (Number.isFinite(liquidityDivisor) && liquidityDivisor > 0) {
      set('obBidLiquidityTotal', point.bidLiquidityTotal / liquidityDivisor)
      set('obAskLiquidityTotal', point.askLiquidityTotal / liquidityDivisor)
    }
    const tier = point.tiers[tierBps]
    if (tier) {
      const denominator = tier.bidVal + tier.askVal
      if (Number.isFinite(denominator) && denominator > 0) {
        set('obDepthImbalance', (tier.bidVal - tier.askVal) / denominator)
      }
    }
    if (Object.keys(values).length > 0) result.push({ timeSec, values })
  }
  return result
}

export const orderbookMetricsSeriesSource: ChartSeriesSource = {
  id: 'orderbookMetrics',
  metrics: [
    'obSpreadBps',
    'obPressureRatio',
    'obBidLiquidityTotal',
    'obAskLiquidityTotal',
    'obDepthImbalance',
  ],
  // Read-only pull endpoint, no live stream in v1 — poll on bar close.
  refresh: 'bar-close',
  minTimeframeSeconds: SERIES_MIN_TIMEFRAME_SECONDS,
  fetchHistory: async (ctx, signal) => {
    const tierBps = resolveDepthTierBps(ctx.inputs)
    const measure = resolveOrderbookLiquidityMeasure(ctx.inputs)
    const request = buildOrderbookMetricsRequest({
      exchange: ctx.exchange,
      symbol: ctx.symbol,
      timeframe: ctx.timeframeSeconds,
      depthTiersBps: [tierBps],
      fromMs: ctx.fromSec * 1000,
      toMs: ctx.toSec * 1000,
    })
    const series = await fetchOrderbookMetricsSeries(request, signal)
    return orderbookMetricsPointsToSeriesPoints(
      orderbookMetricsSeriesToPoints(series),
      tierBps,
      measure
    )
  },
}
